"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { textToSpeechSessionStorage } from "@/services/storage/text-to-speech-session-storage";

export type TextToSpeechSettings = {
  text: string;
  voice: string;
  language: string;
};

type TextToSpeechContextState = {
  settings: TextToSpeechSettings;
  handleTextChange: (text: string) => void;
  handleVoiceChange: (voice: string) => void;
  handleLanguageChange: (language: string) => void;
};

const TextToSpeechContext = createContext<TextToSpeechContextState | undefined>(
  undefined,
);

export const useTextToSpeechContext = () => {
  if (!TextToSpeechContext) {
    throw new Error("TextToSpeechContext is undefined");
  }

  return useContext(TextToSpeechContext)!;
};

const TextToSpeechProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const initialSettings: TextToSpeechSettings = {
    text: "",
    voice: "default",
    language: "English",
  };

  const [settings, setSettings] =
    useState<TextToSpeechSettings>(initialSettings);

  useEffect(() => {
    textToSpeechSessionStorage.setDataFromStorage(setSettings);
  }, []);

  useEffect(() => {
    textToSpeechSessionStorage.settingStorage.setSettings(
      settings.text,
      "text",
    );
    textToSpeechSessionStorage.settingStorage.setSettings(
      settings.voice,
      "voice",
    );
    textToSpeechSessionStorage.settingStorage.setSettings(
      settings.language,
      "language",
    );
  }, [settings.text, settings.voice, settings.language]);

  const handleTextChange = async (text: string) => {
    await textToSpeechSessionStorage.settingStorage.setSettings(text, "text");
    setSettings((prev) => ({ ...prev, text }));
  };

  const handleVoiceChange = async (voice: string) => {
    await textToSpeechSessionStorage.settingStorage.setSettings(voice, "voice");
    setSettings((prev) => ({ ...prev, voice }));
  };

  const handleLanguageChange = async (language: string) => {
    await textToSpeechSessionStorage.settingStorage.setSettings(
      language,
      "language",
    );
    setSettings((prev) => ({ ...prev, language }));
  };

  return (
    <TextToSpeechContext.Provider
      value={{
        settings,
        handleTextChange,
        handleVoiceChange,
        handleLanguageChange,
      }}
    >
      {children}
    </TextToSpeechContext.Provider>
  );
};

export default TextToSpeechProvider;
